import clsx from 'clsx';
import { Bell, CalendarDays, CheckCheck, CircleAlert, Clock, Filter, History, Lock, Megaphone, Server, Unlink, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { NOTIFICATION_CATEGORIES, type NotificationCategory, type NotificationItem, type NotificationSeverity } from '@pz/core';
import type { TFunction } from 'i18next';
import { d, dt, h, kw, month } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { periodText } from './leave';
import { Button, Checkbox, EmptyState, ErrorBox, Loading, Tabs } from './ui';

/**
 * Notification panel: slides in from the right, opened with the bell in the top bar. The content is decided by the
 * server from the rights of the person (see services/notifications.ts); this file only shows it. The category filter
 * is stored on the server per person (follows the person to every device).
 */

interface Cat {
  icon: ReactNode;
  /** chip and symbol in the colour of the category */
  tone: string;
  dot: string;
}

const ICON = 'size-3.5 shrink-0';

/** Categories: colour + symbol + text, never colour alone. */
export const CATEGORY_STYLE: Record<NotificationCategory, Cat> = {
  hours: { icon: <Clock className={ICON} />, tone: 'bg-accent-soft text-accent', dot: 'bg-accent' },
  leave: { icon: <CalendarDays className={ICON} />, tone: 'bg-teal-soft text-teal', dot: 'bg-teal' },
  approvals: { icon: <CheckCheck className={ICON} />, tone: 'bg-violet-soft text-violet', dot: 'bg-violet' },
  period: { icon: <Lock className={ICON} />, tone: 'bg-sand-soft text-sand', dot: 'bg-sand' },
  missing: { icon: <CircleAlert className={ICON} />, tone: 'bg-rose-soft text-rose', dot: 'bg-rose' },
  announcement: { icon: <Megaphone className={ICON} />, tone: 'bg-info-soft text-info', dot: 'bg-info' },
  system: { icon: <Server className={ICON} />, tone: 'bg-sheet-2 text-graphite', dot: 'bg-graphite' },
  forma: { icon: <Unlink className={ICON} />, tone: 'bg-rose-soft text-rose', dot: 'bg-rose' },
};

/** Left edge of an entry: how urgent it is (the category has its own colour in the chip). */
const SEVERITY_EDGE: Record<NotificationSeverity, string> = {
  info: 'border-l-transparent',
  success: 'border-l-good',
  warning: 'border-l-warn',
  critical: 'border-l-bad',
};

/** Notifications that describe a state (not an event): shown without a time of day. */
const STATE_CODE = /^(missing|approvals|period)\./;

/** Text of a notification: the server sends a code and values, the language is chosen here. */
export function notifyText(t: TFunction, item: Pick<NotificationItem, 'code' | 'params' | 'count'>): { text: string; detail: string | null } {
  const p = item.params;
  const v: Record<string, string | number> = { ...p, count: Number(p.count ?? item.count) };
  if (typeof p.week === 'string') v.week = kw(p.week);
  for (const k of ['first', 'last', 'deadline', 'date'] as const) if (typeof p[k] === 'string') v[k] = d(p[k] as string);
  // hours as the language writes them ("42,5"), with a sign where a balance can be negative
  for (const k of ['hours', 'before', 'after'] as const) if (typeof p[k] === 'number') v[k] = `${k !== 'hours' && (p[k] as number) > 0 ? '+' : ''}${h(Math.round((p[k] as number) * 60), 2)}`;
  if (typeof p.month === 'string') v.month = month(`${p.month}-01`);
  if (typeof p.kind === 'string') v.kind = t(`week.absence.${p.kind as 'vacation'}`, { defaultValue: p.kind });
  if (typeof p.from === 'string' && typeof p.to === 'string') v.period = periodText(p.from, p.to);
  if (typeof p.type === 'string') v.type = t(`ann.types.${p.type as 'info'}`, { defaultValue: p.type });
  const text = t(`notify.${item.code}` as 'notify.hours.rejected', { ...v, defaultValue: item.code });
  const detail = typeof p.reason === 'string' && p.reason ? p.reason : typeof p.note === 'string' && p.note ? p.note : null;
  return { text, detail };
}

/* ------------------------------------------------------------------ */
/* Context: open state and unread count for the bell                    */
/* ------------------------------------------------------------------ */

interface NotifyState {
  open: boolean;
  setOpen: (open: boolean) => void;
  unread: number;
}

const NotifyContext = createContext<NotifyState>({ open: false, setOpen: () => {}, unread: 0 });

export function useNotifications() {
  return useContext(NotifyContext);
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const feed = useQ('notifications.feed', {}, { refetchInterval: 120_000 });
  const value = useMemo(() => ({ open, setOpen, unread: feed.data?.unread ?? 0 }), [open, feed.data?.unread]);
  return (
    <NotifyContext.Provider value={value}>
      {children}
      <NotificationPanel />
    </NotifyContext.Provider>
  );
}

export function BellButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { open, setOpen, unread } = useNotifications();
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      aria-label={unread ? t('notif.bellUnread', { count: unread }) : t('notif.title')}
      aria-expanded={open}
      title={t('notif.title')}
      className={clsx('relative inline-flex size-9 items-center justify-center rounded-md text-graphite hover:bg-sheet-2 hover:text-ink', open && 'bg-sheet-2 text-ink', className)}
    >
      <Bell className="size-[18px]" />
      {unread > 0 && (
        <span className="num absolute top-0.5 right-0.5 min-w-4 rounded-full bg-bad px-1 text-center text-[10px] leading-4 font-semibold text-sheet">{unread > 99 ? '99+' : unread}</span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                                */
/* ------------------------------------------------------------------ */

type PanelTab = 'feed' | 'history';

function NotificationPanel() {
  const { t } = useTranslation();
  const { open, setOpen } = useNotifications();
  const [tab, setTab] = useState<PanelTab>('feed');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const raf = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
    };
  }, [open, setOpen]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 print:hidden" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="absolute inset-0 bg-ink/25" onClick={() => setOpen(false)} />
      <aside role="dialog" aria-modal="true" aria-label={t('notif.title')} className="absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col border-l border-line bg-sheet shadow-2xl">
        <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold">{t('notif.title')}</h2>
          <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label={t('common.close')} className="inline-flex size-8 items-center justify-center rounded-md text-graphite hover:bg-sheet-2 hover:text-ink">
            <X className="size-4" />
          </button>
        </header>
        <div className="px-4 pt-2">
          <Tabs
            tabs={[
              { id: 'feed' as const, label: t('notif.tabFeed'), icon: <Bell className="size-3.5" /> },
              { id: 'history' as const, label: t('notif.tabHistory'), icon: <History className="size-3.5" /> },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
        {tab === 'feed' ? <FeedTab /> : <HistoryTab />}
      </aside>
    </div>,
    document.body,
  );
}

function when(item: NotificationItem): string {
  if (STATE_CODE.test(item.code)) return '';
  return dt(item.at);
}

function FeedTab() {
  const { t } = useTranslation();
  const { setOpen } = useNotifications();
  const navigate = useNavigate();
  const feed = useQ('notifications.feed', {});
  const mark = useM('notifications.markRead', { invalidate: ['notifications.', 'home.', 'announcements.'] });
  const annRead = useM('announcements.markRead', { invalidate: ['notifications.', 'home.', 'announcements.'] });
  const setFilter = useM('notifications.setFilter', { invalidate: ['notifications.', 'home.'] });
  const data = feed.data;
  // the filter changes at once on screen; the server confirms with the next answer
  const [local, setLocal] = useState<{ hidden: NotificationCategory[]; onlyUnread: boolean } | null>(null);
  useEffect(() => setLocal(null), [data?.filter]);
  const filter = local ?? data?.filter ?? { hidden: [], onlyUnread: false };

  const save = useCallback(
    (next: { hidden: NotificationCategory[]; onlyUnread: boolean }) => {
      setLocal(next);
      setFilter.mutate(next);
    },
    [setFilter],
  );
  const toggleCategory = (c: NotificationCategory) => save({ ...filter, hidden: filter.hidden.includes(c) ? filter.hidden.filter((x) => x !== c) : [...filter.hidden, c] });

  const items = useMemo(() => {
    if (!data) return [];
    // the list already follows the stored filter; while a change is on its way, show the new one
    return data.items.filter((i) => !filter.hidden.includes(i.category) && (!filter.onlyUnread || !i.read));
  }, [data, filter]);

  const open = (item: NotificationItem) => {
    if (item.category === 'announcement') {
      const m = /^ann:([^:]+):(\d{4}-\d{2}-\d{2}):(\d+)$/.exec(item.key);
      // an announcement that needs a confirmation is read and confirmed on the start page; others are marked read here,
      // for the version the panel shows (a changed announcement is unread again, D-05)
      if (m && !item.actionable && !item.read) annRead.mutate({ id: m[1], occurrence: m[2], revision: Number(m[3]) });
    } else if (!item.read) mark.mutate({ keys: [item.key] });
    setOpen(false);
    navigate(item.link);
  };

  if (feed.isLoading) return <Loading />;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-line px-4 pb-3">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-graphite">
            <Filter className="size-3.5" /> {t('notif.filterTitle')}
          </span>
          {(filter.hidden.length > 0 || filter.onlyUnread) && (
            <button type="button" className="text-xs text-accent hover:underline" onClick={() => save({ hidden: [], onlyUnread: false })}>
              {t('notif.filterReset')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('notif.filterTitle')}>
          {NOTIFICATION_CATEGORIES.map((c) => {
            const count = data?.counts[c];
            // categories that never occur for this person (no right, no data) are not offered
            if (!count || (count.total === 0 && !filter.hidden.includes(c))) return null;
            const on = !filter.hidden.includes(c);
            return (
              <button
                key={c}
                type="button"
                aria-pressed={on}
                onClick={() => toggleCategory(c)}
                title={on ? t('notif.hideCategory', { name: t(`notif.categories.${c}`) }) : t('notif.showCategory', { name: t(`notif.categories.${c}`) })}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                  on ? clsx(CATEGORY_STYLE[c].tone, 'border-transparent') : 'border-line-strong bg-sheet text-faint line-through decoration-faint/60',
                )}
              >
                {CATEGORY_STYLE[c].icon}
                {t(`notif.categories.${c}`)}
                <span className="num">{count.unread > 0 && on ? count.unread : count.total}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <Checkbox label={t('notif.onlyUnread')} checked={filter.onlyUnread} onChange={(e) => save({ ...filter, onlyUnread: e.target.checked })} />
          <Button size="sm" icon={<CheckCheck className="size-3.5" />} disabled={!data?.unread || mark.isPending} onClick={() => mark.mutate({ all: true })}>
            {t('notif.markAll')}
          </Button>
        </div>
        <p className="mt-1.5 text-[11px] text-graphite">{t('notif.filterHint')}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-4 pt-2">
          <ErrorBox error={feed.error ?? mark.error ?? setFilter.error} />
        </div>
        {items.length === 0 ? (
          <div className="p-4">
            <EmptyState>{filter.onlyUnread || filter.hidden.length ? t('notif.emptyFiltered') : t('notif.empty')}</EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line/70">
            {items.map((i) => {
              const { text, detail } = notifyText(t, i);
              const style = CATEGORY_STYLE[i.category];
              return (
                <li key={i.key}>
                  <button
                    type="button"
                    onClick={() => open(i)}
                    className={clsx('flex w-full items-start gap-2.5 border-l-[3px] px-4 py-2.5 text-left hover:bg-sheet-2', SEVERITY_EDGE[i.severity], !i.read && 'bg-accent-soft/30')}
                  >
                    <span className={clsx('mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full', style.tone)} aria-hidden>
                      {style.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={clsx('block text-sm leading-snug', i.read ? 'text-graphite' : 'font-medium text-ink')}>{text}</span>
                      {detail && <span className="mt-0.5 block text-xs text-graphite italic">„{detail}“</span>}
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-faint">
                        <span>{t(`notif.categories.${i.category}`)}</span>
                        {when(i) && <span className="num">{when(i)}</span>}
                        {i.severity === 'critical' && <span className="font-medium text-bad">{t('notif.critical')}</span>}
                        {i.actionable && !i.read && <span className="font-medium text-ink">{t('notif.todo')}</span>}
                      </span>
                    </span>
                    {!i.read && <span className={clsx('mt-2 size-2 shrink-0 rounded-full', style.dot)} aria-label={t('notif.unread')} />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* "Mein Verlauf": own actions                                          */
/* ------------------------------------------------------------------ */

const PAGE = 30;

function valueText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'boolean') return v ? '✓' : '–';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length > 40 ? `${v.slice(0, 38)}…` : v;
  return '…';
}

function HistoryTab() {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(PAGE);
  const q = useQ('history.mine', { limit });
  const rows = q.data?.rows ?? [];
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <p className="px-4 pt-3 pb-2 text-xs text-graphite">{t('notif.historyHint')}</p>
      <div className="px-4">
        <ErrorBox error={q.error} />
      </div>
      {q.isLoading && <Loading />}
      {!q.isLoading && rows.length === 0 && (
        <div className="p-4">
          <EmptyState>{t('notif.historyEmpty')}</EmptyState>
        </div>
      )}
      <ul className="divide-y divide-line/70">
        {rows.map((r) => (
          <li key={r.id} className="px-4 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{t(`notif.actions.${r.action.replace(/\./g, '_')}` as 'notif.actions.time_submit', { defaultValue: r.action })}</span>
              <span className="num shrink-0 text-[11px] text-faint">{dt(r.at)}</span>
            </div>
            {r.label && <div className="truncate text-xs text-graphite">{r.label}</div>}
            {r.changes.length > 0 && (
              <ul className="mt-1 flex flex-col gap-0.5 text-[11px] text-graphite">
                {r.changes.slice(0, 4).map((c) => (
                  <li key={c.field} className="num">
                    {t(`notif.fields.${c.field}` as 'notif.fields.minutes', { defaultValue: c.field.replace(/_/g, ' ') })}: {valueText(c.from)} → {valueText(c.to)}
                  </li>
                ))}
              </ul>
            )}
            {r.reason && <div className="mt-0.5 text-xs text-graphite italic">„{r.reason}“</div>}
          </li>
        ))}
      </ul>
      {q.data?.hasMore && limit < 200 && (
        <div className="p-4">
          <Button className="w-full" onClick={() => setLimit(limit + PAGE)} loading={q.isFetching}>
            {t('notif.historyMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
